import { useMemo } from 'react';

import { instantReplayActiveSwingIdAtom, instantReplayActiveVideosAtom } from '@realtime';
import { useResetAtom } from 'jotai/utils';

import { VideoGrid, VideoPayload } from '@blocks';
import { useTranslate } from '@i18n';
import { useFloorSettings } from '@queries';
import { useEnsureAtomValue } from '@store';

import * as css from './styles.css';
import { VideoStatus } from './VideoStatus';

export const VideoReplay = () => {
    const videos = useEnsureAtomValue(instantReplayActiveVideosAtom);
    const resetVideos = useResetAtom(instantReplayActiveVideosAtom);
    const resetAtoms = useResetAtom(instantReplayActiveSwingIdAtom);
    const { data: floorSettings } = useFloorSettings();
    const t = useTranslate();
    const videoPayload = useMemo(() => {
        if (!floorSettings) {
            return videos.map<VideoPayload>((video) => ({
                url: video.url,
                name: t(`camera_angle.${video.cameraName}`),
                type: 'current',
                id: video.cameraName,
                view: video.view,
            }));
        }

        if (floorSettings.instantPlayback === false) {
            return [];
        }

        if (floorSettings.cameraAngle) {
            const selectedVideo = videos.filter((video) => video.cameraName === floorSettings.cameraAngle) ?? videos;
            return selectedVideo.map<VideoPayload>((video) => ({
                url: video.url,
                name: t(`camera_angle.${video.cameraName}`),
                type: 'current',
                id: video.cameraName,
                view: video.view,
            }));
        }

        return videos.map<VideoPayload>((video) => ({
            url: video.url,
            name: t(`camera_angle.${video.cameraName}`),
            type: 'current',
            id: video.cameraName,
            view: video.view,
        }));
    }, [floorSettings, t, videos]);

    const maxFrame = videos[0]?.metadata.totalFrames;

    const playbackSpeed = floorSettings?.playbackSpeed ?? 1;

    if (!Array.isArray(videos) || !videos.length || !maxFrame) {
        return null;
    }

    const isSingleVideo = videos.length === 1;

    return (
        <div className={css.fullscreenLayout({ columns: 'double' })}>
            <VideoGrid
                mode="autoplay"
                columns={isSingleVideo ? 1 : 2}
                videos={videoPayload}
                maxFrame={maxFrame}
                onReplayEnded={() => {
                    resetAtoms();
                    resetVideos();
                }}
                playbackSpeed={playbackSpeed}
            />
            <VideoStatus />
        </div>
    );
};
